import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  endpoints,
  type DocumentPreview,
  type PreviewEnvelope,
  type QuotationDetail,
} from "@/lib/endpoints";
import {
  discountBody,
  discountInput,
  text,
  usableApiLines,
  type DiscountMode,
  type EditorLine,
} from "@/features/invoices/use-invoice-editor";

/**
 * The quotation editor's state and its wire format (§7, §9.3).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * WHAT THIS SHARES WITH THE INVOICE EDITOR, AND WHAT IT DELIBERATELY DOES NOT.
 *
 * SHARED: everything about a LINE. `EditorLine`, `emptyLine`, `toApiLine` and
 * `usableApiLines` are imported, not reimplemented, because both create routes
 * validate lines with the same `DocumentLineInput` schema — the document kinds
 * differ in their header, never in a line. Two copies of that mapping would
 * drift the first time a field was added to one kind and not the other.
 *
 * NOT SHARED: the header. A quotation carries `subject`, `validUntil`, `poRefNo`,
 * `itemNo` and `taxDisplayMode`; an invoice carries S.O., challan and quotation
 * references. Neither set is meaningful on the other document, and a merged
 * state object would put a "Challan No." on an offer.
 *
 * AND, AS EVERYWHERE: NO TOTAL IS COMPUTED HERE. Every figure comes from
 * `POST /quotations/preview` (CLAUDE.md, §6).
 * ═════════════════════════════════════════════════════════════════════════════
 */

/** §9.3 — how the sheet prints its money. */
export type TaxDisplayMode = "TAX_EXTRA" | "TAX_COMPUTED";

export interface QuotationEditorState {
  customerId: number | null;
  date: string;
  /** `YYYY-MM-DD`, or blank for an offer with no stated expiry. */
  validUntil: string;
  /** The job, in the customer's words — prints as the sheet's title line. */
  subject: string;
  poRefNo: string;
  /**
   * §9.3's `item no`. Blank is a real answer, not a missing one: an empty field
   * prints the footer's write-in rule for a pen, which is what every quotation
   * did before this became typeable.
   *
   * The VENDOR CODE beside it on the printed sheet is deliberately absent from
   * this state. It belongs to the customer (`customers.vendor_code`), the server
   * copies it onto the document at save time, and a per-quotation override would
   * be a second copy of a fact that then drifts.
   */
  itemNo: string;
  attnName: string;
  /**
   * §9.3. TAX_EXTRA prints the PRE-TAX figure under "G. Total" with "GST EXTRA"
   * against the tax rows — the way this business's existing Excel quotations are
   * written, and therefore the default. TAX_COMPUTED prints the tax-inclusive
   * total instead. It changes only what the SHEET SAYS: the engine computes the
   * same tax either way, and the server stores both figures regardless.
   */
  taxDisplayMode: TaxDisplayMode;
  notes: string;
  terms: string;
  /**
   * §15(3)(a) — a discount on the WHOLE quotation, in rupees as typed.
   *
   * IT IS THE MOST VISIBLE FIGURE ON THIS DOCUMENT. Under the default TAX_EXTRA
   * the printed `G. Total` IS the pre-tax subtotal (§9.3), so a discount taken
   * before tax moves the headline number the customer reads — which is exactly
   * what the owner means by "giving a discount".
   *
   * AND IT SURVIVES CONVERSION ONLY WHEN THE INVOICE BILLS THE WHOLE QUOTATION
   * (§7.1). Applying it in full to a partial bill gives it away twice; splitting
   * it invents a number nobody agreed. The server decides that and warns when it
   * drops one — nothing on this screen may pre-empt it.
   */
  discount: string;
  /**
   * §15(3)(a) — the same discount as a human percentage, `"10"`. A negotiated
   * offer is exactly where a round percentage gets agreed over the phone, which
   * is why the toggle matters more here than on a bill.
   *
   * IT IS RESOLVED TO MONEY BY THE SERVER, ONCE, AT SAVE, and it is the money
   * that is stored and printed. Re-saving a revised quotation sends the stored
   * AMOUNT back — see {@link stateFromQuotation} — so the figure the customer
   * accepted cannot move under him, and the server warns when the percentage no
   * longer comes to it.
   */
  discountPercent: string;
  /** Which of the two above crosses the wire — never both (§15(3)(a)). */
  discountMode: DiscountMode;
  lines: EditorLine[];
}

/** A fresh, empty quotation. */
export function emptyQuotationState(today: string, lines: EditorLine[]): QuotationEditorState {
  return {
    customerId: null,
    date: today,
    validUntil: "",
    subject: "",
    poRefNo: "",
    itemNo: "",
    attnName: "",
    taxDisplayMode: "TAX_EXTRA",
    notes: "",
    terms: "",
    discount: "",
    discountPercent: "",
    discountMode: "AMOUNT",
    lines,
  };
}

/**
 * An existing quotation, back into the strings the editor holds — the "edit"
 * path (§7) and the "make a copy" path both land here.
 *
 * `copy` DECIDES WHAT IS DROPPED. Editing keeps the document's own date and
 * validity, because it is still that offer. Copying takes today's date and drops
 * the expiry and the customer's P.O. reference — a fresh offer quoted against
 * last quarter's purchase order is exactly the mismatch that gets a document
 * returned. The NUMBER is never carried either way; a copy allocates its own
 * (§6), and an edit keeps the one already stored server-side.
 *
 * `itemNo` GOES WITH `poRefNo` ON A COPY, for the same reason and not a weaker
 * one: it is the customer's line number WITHIN that purchase order (§9.3 prints
 * the two side by side), so carrying it into a new offer quotes the new work
 * against an old order's item. Dropped, the footer prints its write-in rule
 * again, which is the honest state of a quotation nobody has given a number for
 * yet — not a blank where a value used to be.
 */
export function stateFromQuotation(
  quotation: QuotationDetail,
  lines: EditorLine[],
  options: { copy: boolean; today: string },
): QuotationEditorState {
  return {
    customerId: quotation.customerId,
    date: options.copy ? options.today : quotation.date,
    validUntil: options.copy ? "" : (quotation.validUntil ?? ""),
    subject: quotation.subject ?? "",
    poRefNo: options.copy ? "" : (quotation.poRefNo ?? ""),
    itemNo: options.copy ? "" : (quotation.itemNo ?? ""),
    attnName: quotation.attnName ?? "",
    taxDisplayMode: (quotation.taxDisplayMode as TaxDisplayMode) ?? "TAX_EXTRA",
    notes: quotation.notes ?? "",
    terms: quotation.terms ?? "",
    /*
     * KEPT ON BOTH PATHS, unlike `poRefNo` and `itemNo`. Those two identify the
     * CUSTOMER'S order and belong to the offer that quoted it; a discount is a
     * price this business decided to give, and a copy of a job is a copy of the
     * price it was offered at. The owner can clear the box in one keystroke;
     * silently re-quoting at full rate is the mistake he would not notice.
     */
    discount: discountInput(quotation.totals.documentDiscountPaise),
    /*
     * AS AN AMOUNT, EVEN WHEN 10% IS WHAT WAS TYPED, and on BOTH paths.
     *
     * `totals.documentDiscountPercentBp` is on the wire and is deliberately not
     * read. On the EDIT path re-sending the percentage would make it a fresh
     * input, resolved against the lines as they are now — so revising a line's
     * rate would silently move a discount the customer has already accepted, and
     * it would silence the very warning the server raises to tell him it drifted
     * (`carryDiscountProvenance`). On the COPY path there is no stored document
     * to carry provenance at all, and the price being repeated is the money.
     *
     * The owner can still flip the toggle and retype `10` — that is a NEW
     * decision, which is exactly what re-resolving it against the new lines
     * means, and he made it on purpose.
     */
    discountPercent: "",
    discountMode: "AMOUNT",
    lines,
  };
}

/** The whole quotation as the API takes it, or `null` when not yet sendable. */
export function toQuotationApiBody(
  state: QuotationEditorState,
): Record<string, unknown> | null {
  if (state.customerId === null || state.date.trim() === "") return null;

  // §15(3)(a) — omitted when blank, never zero; held back when it will not
  // parse; ONE key or the other, never both. The same helper the invoice editor
  // uses, sending the same keys to the same mutually-exclusive schema.
  const discount = discountBody(state);
  if (discount === null) return null;

  const lines = usableApiLines(state.lines);
  if (lines === null) return null;

  return {
    ...discount,
    customerId: state.customerId,
    date: state.date,
    validUntil: text(state.validUntil),
    subject: text(state.subject),
    poRefNo: text(state.poRefNo),
    // `text` maps a blank to `undefined`, which the PUT reads as "clear it" — and
    // a cleared item no is the write-in rule back on the printed sheet, not a
    // field that failed to save (§9.3).
    itemNo: text(state.itemNo),
    // NO `vendorCode` KEY, ON PURPOSE. `QuotationCreateSchema` has no such field,
    // the server freezes it from the customer row, and the editor only displays
    // it — sending one would be a client asserting a fact it does not own (§4).
    attnName: text(state.attnName),
    taxDisplayMode: state.taxDisplayMode,
    notes: text(state.notes),
    terms: text(state.terms),
    lines,
  };
}

/**
 * `?editing=12` — the saved quotation this form would replace, or "" for a new one.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * WITHOUT IT THE PREVIEW DESCRIBES A DIFFERENT DOCUMENT FROM THE SAVE, and the
 * difference is the tax split on a page the customer receives.
 *
 * §4 freezes the buyer onto a saved quotation, so `PUT /quotations/:id` keeps the
 * STORED snapshot whenever the request names the same customer — which is always,
 * because {@link toQuotationApiBody} sends back the `customerId` the editor
 * loaded. The preview routes used to read the LIVE `customers` row instead, so a
 * buyer whose GSTIN had been corrected from a Maharashtra one to a Gujarat one
 * previewed as "IGST 18%" and saved as CGST+SGST.
 *
 * A QUERY PARAMETER, not a body key: the preview validates the create schema
 * verbatim so it cannot accept what the save would reject.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export function editingQuery(editingId: string | number | undefined): string {
  if (editingId === undefined || editingId === "") return "";
  return `?editing=${String(editingId)}`;
}

/**
 * The server's figures for the quotation as it currently stands.
 *
 * `POST /quotations/preview` WRITES NOTHING AND ALLOCATES NO NUMBER — the
 * counter is untouched, so a quotation that is previewed twenty times and never
 * saved leaves no gap in the series (§6).
 *
 * `editingId` IS THE ID FROM THE ROUTE when revising a saved quotation — see
 * {@link editingQuery} for why the figures are wrong without it. Undefined for a
 * new one, which is the create path and unaffected.
 *
 * DEBOUNCED at 400ms, matching the invoice editor: long enough that typing a
 * rate does not fire six requests, short enough that the total stays attached to
 * the keystroke.
 */
export function useQuotationPreview(state: QuotationEditorState, editingId?: string) {
  const body = toQuotationApiBody(state);
  const [debounced, setDebounced] = useState<string | null>(null);

  const serialised = body === null ? null : JSON.stringify(body);
  const suffix = editingQuery(editingId);

  useEffect(() => {
    if (serialised === null) {
      setDebounced(null);
      return;
    }
    const timer = window.setTimeout(() => {
      setDebounced(serialised);
    }, 400);
    return () => {
      window.clearTimeout(timer);
    };
  }, [serialised]);

  return useQuery({
    // The suffix is part of the key: the same lines previewed against a saved
    // quotation and against a new one are two different answers.
    queryKey: ["quotations", "preview", suffix, debounced],
    queryFn: () =>
      api.post<PreviewEnvelope<DocumentPreview>>(
        `${endpoints.quotationPreview}${suffix}`,
        JSON.parse(debounced ?? "{}") as unknown,
      ),
    enabled: debounced !== null,
    // The last good figures stay on screen while the next preview is in flight,
    // so the total does not blink to nothing between keystrokes.
    placeholderData: keepPreviousData,
    // A rejected preview is information, not a transient failure to retry.
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
  });
}

/**
 * The save — `POST /quotations` for a new one, `PUT /quotations/:id` for an
 * edit.
 *
 * THE `PUT` IS WHAT AN INVOICE HAS NO EQUIVALENT OF, and the difference is not
 * an oversight in either direction. An issued invoice is a tax document; it is
 * corrected by a credit note and `invoice.edit` does not exist as a capability
 * for anyone (§2.1, CLAUDE.md). A quotation is an OFFER, and revising an offer
 * before it is accepted is the ordinary course of business — so the route exists,
 * and the server gates it on status (DRAFT or SENT only), not on a capability
 * this screen could talk its way past.
 *
 * A `PUT` REPLACES THE LINES WHOLESALE and does NOT touch the number: the
 * quotation keeps 542/26 across every revision, which is what the customer has
 * in their inbox.
 */
export function useSaveQuotation(id: string | undefined) {
  const queryClient = useQueryClient();
  const isEdit = id !== undefined && id !== "";

  return useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      isEdit
        ? api.put<PreviewEnvelope<QuotationDetail>>(endpoints.quotation(id), body)
        : api.post<PreviewEnvelope<QuotationDetail>>(endpoints.quotations, body),
    onSuccess: () => {
      /*
       * EVERYTHING, for the same reason the invoice save invalidates everything:
       * a saved quotation changes the register, the dashboard's counts and the
       * recent list, and a narrower invalidation leaves the owner looking at a
       * screen that does not include the document he just saved — which reads as
       * the save having failed.
       */
      void queryClient.invalidateQueries();
    },
  });
}
